import { useState, useEffect } from 'react'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'

const NAV = [
  { key: 'novedades', label: 'Novedades', icon: '📝' },
  { key: 'accesos', label: 'Accesos', icon: '↑' },
  { key: 'gremios', label: 'Gremios', icon: '🔧' },
  { key: 'paquetes', label: 'Paquetería', icon: '📦' },
  { key: 'retiros', label: 'Retiros', icon: '📤' },
  { key: 'reclamos', label: 'Reclamos', icon: '⚠️' },
  { key: 'difusiones', label: 'Difusiones', icon: '📨' },
  { key: 'directorio', label: 'Directorio', icon: '👥' },
  { key: 'historial', label: 'Historial', icon: '📋' },
  { key: 'oficinas', label: 'Oficinas', icon: '🏢' },
  { key: 'dashboard', label: 'Dashboard', icon: '📊' },
]

const NAV_RAWSON = [
  { key: 'salas', label: 'Salas', icon: '📅' },
]

const NAV_ADMIN = [
  { key: 'usuarios', label: 'Usuarios', icon: '⚙️' },
]

export default function Layout({ children, currentPage, onNavigate }) {
  const { usuario, logout } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [contadorNovedades, setContadorNovedades] = useState(0)
  const isAdmin = usuario?.rol === 'admin'
  const edificioId = isAdmin ? null : usuario?.edificios?.id

  // Mostrar salas si es admin o si su edificio es Rawson
  const esRawson = isAdmin || usuario?.edificios?.nombre?.toLowerCase().includes('rawson')

  // Cargar contador de novedades activas
  useEffect(() => {
    const cargarContador = async () => {
      let query = supabase
        .from('novedades')
        .select('id', { count: 'exact' })
        .eq('estado', 'activa')  // Cambiado a estado='activa'
      
      if (!isAdmin && edificioId) {
        query = query.eq('edificio_id', edificioId)
      }
      
      const { count } = await query
      setContadorNovedades(count || 0)
    }
    
    if (usuario) {
      cargarContador()
      
      // Suscribirse a cambios en novedades
      const channel = supabase
        .channel('novedades-changes')
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'novedades' },
          () => cargarContador()
        )
        .subscribe()
      
      return () => {
        channel.unsubscribe()
      }
    }
  }, [usuario, isAdmin, edificioId])

  // NAV items con contador dinámico
  const navItems = [
    { key: 'novedades', label: `Novedades${contadorNovedades > 0 ? ` (${contadorNovedades})` : ''}`, icon: '📝' },
    { key: 'accesos', label: 'Accesos', icon: '↑' },
    { key: 'gremios', label: 'Gremios', icon: '🔧' },
    { key: 'paquetes', label: 'Paquetería', icon: '📦' },
    { key: 'retiros', label: 'Retiros', icon: '📤' },
    { key: 'reclamos', label: 'Reclamos', icon: '⚠️' },
    { key: 'difusiones', label: 'Difusiones', icon: '📨' },
    { key: 'directorio', label: 'Directorio', icon: '👥' },
    { key: 'historial', label: 'Historial', icon: '📋' },
    { key: 'oficinas', label: 'Oficinas', icon: '🏢' },
    { key: 'dashboard', label: 'Dashboard', icon: '📊' },
  ]

  const handleNav = (key) => { onNavigate(key); setMenuOpen(false) }

  const NavItem = ({ item }) => (
    <button onClick={() => handleNav(item.key)} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 14px', borderRadius: 8, cursor: 'pointer', border: 'none',
      background: currentPage === item.key ? 'var(--gold-dim)' : 'transparent',
      color: currentPage === item.key ? 'var(--gold)' : 'var(--text3)',
      fontSize: 13, fontWeight: currentPage === item.key ? 600 : 400,
      transition: 'all 0.15s', textAlign: 'left',
      borderLeft: currentPage === item.key ? '2px solid var(--gold)' : '2px solid transparent',
    }}>
      <span style={{ width: 20, textAlign: 'center', fontSize: 15 }}>{item.icon}</span>
      {item.label}
    </button>
  )

  const SidebarContent = () => (
    <>
      <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--border)' }}>
        <img src="/logo.png" alt="Edificios Essence" style={{ width: '100%', maxWidth: 140, display: 'block' }} />
      </div>
      {usuario && (
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 4 }}>
            {isAdmin ? 'ADMINISTRADOR' : 'EDIFICIO'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 500 }}>
            {isAdmin ? 'Acceso total' : usuario.edificios?.nombre}
          </div>
        </div>
      )}
      <nav style={{ flex: 1, padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' }}>
        {navItems.map(item => <NavItem key={item.key} item={item} />)}

        {esRawson && (
          <>
            <div style={{ margin: '16px 0 8px', padding: '0 2px', fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1 }}>RAWSON</div>
            {NAV_RAWSON.map(item => <NavItem key={item.key} item={item} />)}
          </>
        )}

        {isAdmin && (
          <>
            <div style={{ margin: '16px 0 8px', padding: '0 2px', fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', letterSpacing: 1 }}>ADMINISTRACIÓN</div>
            {NAV_ADMIN.map(item => <NavItem key={item.key} item={item} />)}
          </>
        )}
      </nav>
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4, fontWeight: 500 }}>{usuario?.nombre}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>
          {new Date().toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={logout} style={{ width: '100%', fontSize: 12 }}>Cerrar sesión</button>
      </div>
    </>
  )

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <aside style={{ width: 220, background: 'var(--bg2)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', position: 'fixed', height: '100vh', zIndex: 50, left: 0, top: 0 }} className="mobile-hide">
        <SidebarContent />
      </aside>

      <header style={{ display: 'none', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: 'var(--bg2)', borderBottom: '1px solid var(--border)', padding: '12px 16px', alignItems: 'center', justifyContent: 'space-between' }} className="mobile-header">
        <img src="/logo.png" alt="Edificios Essence" style={{ height: 28 }} />
        <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', fontSize: 22, padding: 4 }}>
          {menuOpen ? '✕' : '☰'}
        </button>
      </header>

      {menuOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99, background: 'rgba(0,0,0,0.6)' }} onClick={() => setMenuOpen(false)}>
          <div style={{ width: 260, height: '100vh', background: 'var(--bg2)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <SidebarContent />
          </div>
        </div>
      )}

      <main style={{ marginLeft: 220, flex: 1, padding: '32px 36px', minHeight: '100vh' }} className="main-content">
        {children}
      </main>

      <style>{`
        @media (max-width: 768px) {
          .mobile-hide { display: none !important; }
          .mobile-header { display: flex !important; }
          .main-content { margin-left: 0 !important; padding: 72px 16px 24px !important; }
        }
      `}</style>
    </div>
  )
}
